<template>
    <div
        class="flex flex-col items-center gap-4 p-6 pt-6 text-lg dark:bg-gray-800 dark:text-gray-100">
        <language-dropdown
            class="mb-6"
            :show-text="true"
            size="base"
            :lang="lang"
            dropdown-classes="max-h-[250px]"
            @update="onLangUpdate"></language-dropdown>

        <button
            type="button"
            class="text-white bg-gradient-to-br from-purple-600 to-blue-500 hover:bg-gradient-to-bl focus:ring-4 focus:outline-none focus:ring-blue-300 dark:focus:ring-blue-800 font-medium rounded-lg text-lg px-5 py-2.5 inline-flex gap-2 justify-center items-center cursor-pointer"
            @click="showContentApp">
            <i-tabler-upload class="w-5 h-5"></i-tabler-upload>
            {{ $t('popup.button_label') }}
        </button>

        <!-- <div class="mb-2 bg-gray-100 p-4 rounded" v-if="openAppError">
            <div class="text-red-700 text-base text-center">
                {{ openAppError }}
            </div>
            <div class="text-base text-center mt-1">
                Please navigate to any active website, such as
                <span
                    class="underline hover:no-underline cursor-pointer"
                    @click="openUrl($event, 'https://google.com')"
                    >google.com</span
                >, and then try once again. Click
                <span
                    class="underline hover:no-underline cursor-pointer"
                    @click="openSupportPage"
                    >here</span
                >
                for an explanation.
            </div>
        </div> -->

        <div class="text-center mt-6">
            <label
                for="quality"
                class="block mb-1 text-gray-900 dark:text-gray-100 font-medium text-base"
                >{{ $t('popup.jpg_quality_label') }}</label
            >
            <div class="flex items-baseline gap-2">
                <input
                    id="quality"
                    type="range"
                    :value="jpgQuality"
                    @input="onJpgQualityChange"
                    min="0"
                    max="100"
                    class="w-full h-2 mb-6 bg-gray-200 rounded-lg appearance-none cursor-pointer dark:bg-gray-700" />
                <span class="text-base">{{ jpgQuality }}%</span>
            </div>
        </div>

        <div class="flex items-center gap-2 mt-6">
            <div
                class="group/help text-center hover:opacity-100 cursor-pointer"
                @click="openSupportPage">
                <span class="opacity-60 group-hover/help:opacity-100 mr-1-"
                    >💡</span
                >
                <span
                    class="underline group-hover/help:no-underline text-gray-500 group-hover/help:text-gray-600"
                    >{{ $t('popup.help_label') }}</span
                >
            </div>

            <!-- <span class="text-gray-400">&bull;</span> -->
        </div>

        <div
            class="text-sm mt-5 mb-2 text-gray-500 dark:text-gray-400 flex flex-col items-center">
            <div class="flex gap-1 items-center">
                {{ $t('popup.support_email_label') }}
            </div>
            <a
                class="underline hover:no-underline"
                :href="`mailto:${getSupportEmail()}`"
                >{{ getSupportEmail() }}</a
            >
        </div>
    </div>
</template>

<script setup lang="ts">
import { detectLangOnPageLoad, setI18nLanguage } from '~/helpers/i18n';
import { saveToStorage } from '~/helpers/storage';
import { LanguageCode } from '~/helpers/types';
import {
    isInExtension,
    getSupportEmail,
    openHelpPage,
    isDevelopment,
} from '~/helpers/utils';
import * as amplitude from '@amplitude/analytics-browser';

const jpgQuality = ref(90);
const lang = ref<LanguageCode>('en');

// read settings from storage if available
if (isInExtension()) {
    chrome.storage.sync.get(['jpgQuality'], (result) => {
        let value = parseInt(result.jpgQuality ?? 90);
        if (isNaN(value)) {
            value = 90;
        }
        jpgQuality.value = value;
    });
}

// on change of jpg quality
const onJpgQualityChange = (event: Event) => {
    const target = event.target as HTMLInputElement;
    jpgQuality.value = parseInt(target.value);
    amplitude.track(`Action popup: jpg quality changed to ${jpgQuality.value}`);
    // save settings if in extension
    if (isInExtension()) {
        chrome.storage.sync.set({ jpgQuality: jpgQuality.value }, () => {
            // callback on save
        });
    }
};

// const openAppError = ref('');

// show content app
const showContentApp = async () => {
    if (!isInExtension()) {
        // openAppError.value = 'Unable to launch the converter on this page.';
        openUrl(new MouseEvent('click'), '/content.html');
        return;
    }
    const response: any = await chrome.runtime.sendMessage({
        message: 'show_on_page_content',
    });
    if (response?.error) {
        // Instead of showing error, open the converter in a new tab
        // if (response.error.includes('Unable to launch')) {
        chrome.runtime.sendMessage({
            message: 'show_converter_in_new_tab',
        });
        // } else {
        //     openAppError.value = response.error;
        // }
        return;
    }
    setTimeout(() => {
        window.close();
    }, 10);
};

const openSupportPage = (event: MouseEvent) => {
    amplitude.track(`Action popup: open help page`);

    openHelpPage();

    // if (isInExtension()) {
    //     chrome.tabs.create({
    //         active: true,
    //         url: chrome.runtime.getURL('/support.html'),
    //     });
    //     return;
    // }
    // window.open('/support.html', '_blank');
};

const openUrl = (event: MouseEvent, url: string) => {
    if (isInExtension()) {
        chrome.tabs.create({
            active: true,
            url: url,
        });
        return;
    }
    window.open(url, '_blank');
};

const onLangUpdate = (newLang: string) => {
    // update global var
    lang.value = newLang as LanguageCode;

    saveToStorage('lang', newLang);

    amplitude.track(`Action popup: language changed to ${newLang}`);
};

onMounted(async () => {
    amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
        autocapture: false,
    });

    let langDetected = await detectLangOnPageLoad();

    if (langDetected) {
        if (isDevelopment()) {
            console.log('[DEV] lang detected', langDetected);
        }
        lang.value = langDetected as LanguageCode;
        setI18nLanguage(langDetected);
    }
});
</script>
